<template>
  <q-form
    @submit="filterSearchResults"
    @reset="resetFilters"
    class="filters-sidebar"
    :disable="!searchLoaded"
  >
    <div class="filters-sidebar__card">
      <q-list>
        <q-expansion-item
          expand-separator
          label="Location & Distance"
          default-opened
        >
          <q-item>
            <q-item-section>
              <q-input
                dense
                standout
                v-model="postalCode"
                label="Zip Code"
                :error="location.postalCodeInput.error != null"
                :disable="!searchLoaded || location.postalCodeInput.disabled"
                inputmode="numeric"
                pattern="[0-9]*"
                mask="#####"
                no-error-icon
                class="q-pb-sm q-mb-xs"
              >
                <template v-slot:append>
                  <q-btn
                    dense
                    icon="sym_r_search"
                    type="submit"
                    color="secondary"
                    class="q-my-sm q-ml-sm"
                    :disable="
                      !searchLoaded ||
                        location.postalCodeInput.disabled ||
                        location.postalCodeInput.disableSubmit
                    "
                    style="padding: 2px 8px;"
                    @click="updateSearchLocation()"
                  />
                </template>
              </q-input>
              <q-select
                dense
                standout
                v-model="radius"
                :options="searchFilterOptions.locationRadius"
                label="Search Within"
                :disable="
                  !searchLoaded ||
                    location.postalCodeInput.disabled ||
                    location.postalCodeInput.disableSubmit
                "
                emit-value
                map-options
              />
            </q-item-section>
          </q-item>
        </q-expansion-item>

        <q-expansion-item expand-separator label="Make & Model">
          <div
            v-if="
              !searchLoaded && searchFilterOptions.makeModelsNested.length == 0
            "
            class="flex justify-center q-pa-md"
          >
            <q-spinner size="2rem" color="grey-7" />
          </div>

          <q-expansion-item
            v-for="makeOption in searchFilterOptions.makeModelsNested"
            :key="makeOption.value + '_makeSearchFilterOption'"
            :header-inset-level="0"
            dense
            dense-toggle
            expand-separator
          >
            <template v-slot:header>
              <q-item-section>
                <q-item-label>{{ makeOption.label }}</q-item-label>
              </q-item-section>
              <q-item-section side>
                <small v-if="searchLoaded">{{ makeOption.count }}</small>
                <q-skeleton
                  v-else
                  type="text"
                  class="text-subtitle1"
                  animation="fade"
                  style="width: 28px"
                />
              </q-item-section>
            </template>

            <q-item
              :key="makeOption.value + '_all_modelSearchFilterOption'"
              :inset-level="0.1"
              v-ripple
              tag="label"
              dense
              :disable="!searchLoaded"
            >
              <q-item-section side>
                <q-checkbox
                  dense
                  v-model="searchFilterModels.makeId"
                  :val="makeOption.value"
                  color="secondary"
                  :disable="!searchLoaded"
                  @click="filterSearchResults"
                />
              </q-item-section>
              <q-item-section>
                All Models
              </q-item-section>
              <q-item-section side>
                <small v-if="searchLoaded">{{ makeOption.count }}</small>
                <q-skeleton
                  v-else
                  type="text"
                  class="text-subtitle1"
                  animation="fade"
                  style="width: 28px"
                />
              </q-item-section>
            </q-item>

            <div
              v-for="modelOption in makeOption.modelOptions"
              :key="
                makeOption.value +
                  '_' +
                  modelOption.value +
                  '_modelSearchFilterOption'
              "
            >
              <q-item
                :inset-level="0.1"
                v-ripple
                tag="label"
                dense
                :disable="!searchLoaded"
              >
                <q-item-section side>
                  <q-checkbox
                    dense
                    v-model="searchFilterModels.modelId"
                    :val="modelOption.value"
                    color="secondary"
                    :disable="!searchLoaded"
                    @click="filterSearchResults"
                  />
                </q-item-section>
                <q-item-section>
                  <span class="ellipsis full-width">{{
                    modelOption.label
                  }}</span>
                </q-item-section>
                <q-item-section side>
                  <small v-if="searchLoaded">{{ modelOption.count }}</small>
                  <q-skeleton
                    v-else
                    type="text"
                    class="text-subtitle1"
                    animation="fade"
                    style="width: 28px"
                  />
                </q-item-section>
              </q-item>
              <div
                v-if="searchFilterModels.modelId.includes(modelOption.value)"
              >
                <q-item
                  v-for="trimOption in searchFilterOptions.modelIdTrim[
                    modelOption.value
                  ]"
                  :key="
                    modelOption.value +
                      '_' +
                      trimOption.value +
                      '_modelTrimSearchFilterOption'
                  "
                  :inset-level="0.3"
                  v-ripple
                  tag="label"
                  dense
                  :disable="!searchLoaded"
                >
                  <q-item-section side>
                    <q-checkbox
                      dense
                      v-model="searchFilterModels.trim"
                      :val="trimOption.value"
                      color="secondary"
                      :disable="!searchLoaded"
                      @click="filterSearchResults"
                    />
                  </q-item-section>
                  <q-item-section>
                    <span class="ellipsis full-width">{{
                      trimOption.label
                    }}</span>
                  </q-item-section>
                  <q-item-section side>
                    <small v-if="searchLoaded">{{ trimOption.count }}</small>
                    <q-skeleton
                      v-else
                      type="text"
                      class="text-subtitle1"
                      animation="fade"
                      style="width: 28px"
                    />
                  </q-item-section>
                </q-item>
              </div>
            </div>
          </q-expansion-item>
        </q-expansion-item>

        <q-expansion-item expand-separator label="Body Type">
          <div
            v-if="!searchLoaded && searchFilterOptions.bodyType.length == 0"
            class="flex justify-center q-pa-md"
          >
            <q-spinner size="2rem" color="grey-7" />
          </div>
          <q-item
            v-for="(option, i) in searchFilterOptions.bodyType"
            :key="i + '_bodyTypeSearchFilterOption'"
            :inset-level="0"
            v-ripple
            tag="label"
            dense
            :disable="!searchLoaded"
          >
            <q-item-section side>
              <q-checkbox
                dense
                v-model="searchFilterModels.bodyType"
                :val="option.value"
                color="secondary"
                :disable="!searchLoaded"
                @click="filterSearchResults"
              />
            </q-item-section>
            <q-item-section>
              {{ option.label }}
            </q-item-section>
            <q-item-section side>
              <small v-if="searchLoaded">{{ option.count }}</small>
              <q-skeleton
                v-else
                type="text"
                class="text-subtitle1"
                animation="fade"
                style="width: 28px"
              />
            </q-item-section>
          </q-item>
        </q-expansion-item>

        <q-expansion-item expand-separator label="Condition">
          <div
            v-if="!searchLoaded && searchFilterOptions.condition.length == 0"
            class="flex justify-center q-pa-md"
          >
            <q-spinner size="2rem" color="grey-7" />
          </div>
          <q-item
            v-for="(option, i) in searchFilterOptions.condition"
            :key="i + '_conditionSearchFilterOption'"
            :inset-level="0"
            v-ripple
            tag="label"
            dense
            :disable="!searchLoaded"
          >
            <q-item-section side>
              <q-checkbox
                dense
                v-model="searchFilterModels.condition"
                :val="option.value"
                color="secondary"
                :disable="!searchLoaded"
                @click="filterSearchResults"
              />
            </q-item-section>
            <q-item-section>
              {{ option.label }}
            </q-item-section>
            <q-item-section side>
              <small v-if="searchLoaded">{{ option.count }}</small>
              <q-skeleton
                v-else
                type="text"
                class="text-subtitle1"
                animation="fade"
                style="width: 28px"
              />
            </q-item-section>
          </q-item>
        </q-expansion-item>

        <q-expansion-item expand-separator label="Price">
          <RangeWithInputs
            v-model="searchFilterModels.price"
            input-label="Price"
            :min="searchFilterOptions.price.min"
            :max="searchFilterOptions.price.max"
            @change="filterSearchResults"
            :step-size="1000"
            input-prefix="$"
            number-formatted-input
            prevent-min-max-overlap
            :disabled="!searchLoaded"
          />
        </q-expansion-item>

        <q-expansion-item expand-separator label="Mileage">
          <RangeWithInputs
            v-model="searchFilterModels.mileage"
            input-label="Mileage"
            :min="searchFilterOptions.mileage.min"
            :max="searchFilterOptions.mileage.max"
            @change="filterSearchResults"
            :step-size="1000"
            number-formatted-input
            prevent-min-max-overlap
            :disabled="!searchLoaded"
          />
        </q-expansion-item>

        <q-expansion-item expand-separator label="Year">
          <RangeWithInputs
            v-model="searchFilterModels.year"
            input-label="Year"
            :min="searchFilterOptions.year.min"
            :max="searchFilterOptions.year.max"
            @change="filterSearchResults"
            :step-size="1"
            strict-range
            :disabled="!searchLoaded"
          />
        </q-expansion-item>

        <q-expansion-item expand-separator label="Deal Score">
          <div
            v-if="
              !searchLoaded && searchFilterOptions.dealScorePhrase.length == 0
            "
            class="flex justify-center q-pa-md"
          >
            <q-spinner size="2rem" color="grey-7" />
          </div>
          <q-item
            v-for="(option, i) in searchFilterOptions.dealScorePhrase"
            :key="i + '_dealScorePhraseSearchFilterOption'"
            :inset-level="0"
            v-ripple
            tag="label"
            dense
            :disable="!searchLoaded"
          >
            <q-item-section side>
              <q-checkbox
                dense
                v-model="searchFilterModels.dealScorePhrase"
                :val="option.value"
                color="secondary"
                :disable="!searchLoaded"
                @click="filterSearchResults"
              />
            </q-item-section>
            <q-item-section>
              <div>
                <ScoreBadge
                  :score="convertScorePhraseToScore(option.label)"
                  style="cursor: pointer"
                />
              </div>
            </q-item-section>
            <q-item-section side>
              <small v-if="searchLoaded">{{ option.count }}</small>
              <q-skeleton
                v-else
                type="text"
                class="text-subtitle1"
                animation="fade"
                style="width: 28px"
              />
            </q-item-section>
          </q-item>
        </q-expansion-item>

        <q-expansion-item expand-separator label="Features">
          <div
            v-if="
              (!searchLoaded || featuresFilterOptionsLoading) &&
                searchFilterOptions.features.length == 0
            "
            class="flex justify-center q-pa-md"
          >
            <q-spinner size="2rem" color="grey-7" />
          </div>
          <q-item
            v-for="(option, i) in searchFilterOptions.features"
            :key="i + '_featureSearchFilterOption'"
            :inset-level="0"
            v-ripple
            tag="label"
            dense
            :disable="!searchLoaded || featuresFilterOptionsLoading"
          >
            <q-item-section side>
              <q-checkbox
                dense
                v-model="searchFilterModels[option.key]"
                :true-value="1"
                :false-value="''"
                color="secondary"
                :disable="!searchLoaded || featuresFilterOptionsLoading"
                @click="filterSearchResults"
              />
            </q-item-section>
            <q-item-section>
              {{ option.label }}
            </q-item-section>
            <q-item-section side>
              <small v-if="searchLoaded && !featuresFilterOptionsLoading">{{
                option.count
              }}</small>
              <q-skeleton
                v-else
                type="text"
                class="text-subtitle1"
                animation="fade"
                style="width: 28px"
              />
            </q-item-section>
          </q-item>
        </q-expansion-item>

        <q-expansion-item expand-separator label="Exterior Color">
          <div
            v-if="
              !searchLoaded && searchFilterOptions.exteriorColor.length == 0
            "
            class="flex justify-center q-pa-md"
          >
            <q-spinner size="2rem" color="grey-7" />
          </div>
          <q-item
            v-for="(option, i) in searchFilterOptions.exteriorColor"
            :key="i + '_colorSearchFilterOption'"
            :inset-level="0"
            v-ripple
            tag="label"
            dense
            :disable="!searchLoaded"
          >
            <q-item-section side>
              <q-checkbox
                dense
                v-model="searchFilterModels.exteriorColor"
                :val="option.value"
                color="secondary"
                :disable="!searchLoaded"
                @click="filterSearchResults"
              />
            </q-item-section>
            <q-item-section>
              {{ option.label }}
            </q-item-section>
            <q-item-section side>
              <small v-if="searchLoaded">{{ option.count }}</small>
              <q-skeleton
                v-else
                type="text"
                class="text-subtitle1"
                animation="fade"
                style="width: 28px"
              />
            </q-item-section>
            <q-item-section side>
              <div
                v-if="option.value === 'Brown'"
                style="background: #DBC5A4; border-radius: 200px; width: 20px; height: 20px; position: relative"
                class="--bordered"
              >
                <div
                  style="position: absolute; top: 0; left: 0; width: 50%; height: 100%; background: #914915; border-top-left-radius: 200px; border-bottom-left-radius: 200px"
                ></div>
              </div>
              <div
                v-else
                :style="{ background: option.color }"
                style="border-radius: 200px; width: 20px; height: 20px"
                class="--bordered"
              />
            </q-item-section>
          </q-item>
        </q-expansion-item>

        <q-expansion-item expand-separator label="Interior Color">
          <div
            v-if="
              !searchLoaded && searchFilterOptions.interiorColor.length == 0
            "
            class="flex justify-center q-pa-md"
          >
            <q-spinner size="2rem" color="grey-7" />
          </div>
          <q-item
            v-for="(option, i) in searchFilterOptions.interiorColor"
            :key="i + '_interiorColorSearchFilterOption'"
            :inset-level="0"
            v-ripple
            tag="label"
            dense
            :disable="!searchLoaded"
          >
            <q-item-section side>
              <q-checkbox
                dense
                v-model="searchFilterModels.interiorColor"
                :val="option.value"
                color="secondary"
                :disable="!searchLoaded"
                @click="filterSearchResults"
              />
            </q-item-section>
            <q-item-section>
              {{ option.label }}
            </q-item-section>
            <q-item-section side>
              <small v-if="searchLoaded">{{ option.count }}</small>
              <q-skeleton
                v-else
                type="text"
                class="text-subtitle1"
                animation="fade"
                style="width: 28px"
              />
            </q-item-section>
            <q-item-section side>
              <div
                v-if="option.value === 'Brown'"
                style="background: #DBC5A4; border-radius: 200px; width: 20px; height: 20px; position: relative"
                class="--bordered"
              >
                <div
                  style="position: absolute; top: 0; left: 0; width: 50%; height: 100%; background: #914915; border-top-left-radius: 200px; border-bottom-left-radius: 200px"
                ></div>
              </div>
              <div
                v-else
                :style="{ background: option.color }"
                style="border-radius: 200px; width: 20px; height: 20px"
                class="--bordered"
              />
            </q-item-section>
          </q-item>
        </q-expansion-item>

        <q-expansion-item expand-separator label="Fuel Type">
          <div
            v-if="!searchLoaded && searchFilterOptions.fuelType.length == 0"
            class="flex justify-center q-pa-md"
          >
            <q-spinner size="2rem" color="grey-7" />
          </div>
          <q-item
            v-for="(option, i) in searchFilterOptions.fuelType"
            :key="i + '_fuelTypeSearchFilterOption'"
            :inset-level="0"
            v-ripple
            tag="label"
            dense
            :disable="!searchLoaded"
          >
            <q-item-section side>
              <q-checkbox
                dense
                v-model="searchFilterModels.fuelType"
                :val="option.value"
                color="secondary"
                :disable="!searchLoaded"
                @click="filterSearchResults"
              />
            </q-item-section>
            <q-item-section>
              {{ option.label }}
            </q-item-section>
            <q-item-section side>
              <small v-if="searchLoaded">{{ option.count }}</small>
              <q-skeleton
                v-else
                type="text"
                class="text-subtitle1"
                animation="fade"
                style="width: 28px"
              />
            </q-item-section>
          </q-item>
        </q-expansion-item>

        <q-expansion-item expand-separator label="Drivetrain">
          <div
            v-if="!searchLoaded && searchFilterOptions.drivetrain.length == 0"
            class="flex justify-center q-pa-md"
          >
            <q-spinner size="2rem" color="grey-7" />
          </div>
          <q-item
            v-for="(option, i) in searchFilterOptions.drivetrain"
            :key="i + '_drivetrainSearchFilterOption'"
            :inset-level="0"
            v-ripple
            tag="label"
            dense
            :disable="!searchLoaded"
          >
            <q-item-section side>
              <q-checkbox
                dense
                v-model="searchFilterModels.drivetrain"
                :val="option.value"
                color="secondary"
                :disable="!searchLoaded"
                @click="filterSearchResults"
              />
            </q-item-section>
            <q-item-section>
              {{ option.label }}
            </q-item-section>
            <q-item-section side>
              <small v-if="searchLoaded">{{ option.count }}</small>
              <q-skeleton
                v-else
                type="text"
                class="text-subtitle1"
                animation="fade"
                style="width: 28px"
              />
            </q-item-section>
          </q-item>
        </q-expansion-item>

        <q-expansion-item expand-separator label="Transmission">
          <div
            v-if="!searchLoaded && searchFilterOptions.transmission.length == 0"
            class="flex justify-center q-pa-md"
          >
            <q-spinner size="2rem" color="grey-7" />
          </div>
          <q-item
            v-for="(option, i) in searchFilterOptions.transmission"
            :key="i + '_transmissionSearchFilterOption'"
            :inset-level="0"
            v-ripple
            tag="label"
            dense
            :disable="!searchLoaded"
          >
            <q-item-section side>
              <q-checkbox
                dense
                v-model="searchFilterModels.transmission"
                :val="option.value"
                color="secondary"
                :disable="!searchLoaded"
                @click="filterSearchResults"
              />
            </q-item-section>
            <q-item-section>
              {{ option.label }}
            </q-item-section>
            <q-item-section side>
              <small v-if="searchLoaded">{{ option.count }}</small>
              <q-skeleton
                v-else
                type="text"
                class="text-subtitle1"
                animation="fade"
                style="width: 28px"
              />
            </q-item-section>
          </q-item>
        </q-expansion-item>

        <q-expansion-item expand-separator label="Engine Type">
          <div
            v-if="!searchLoaded && searchFilterOptions.engineType.length == 0"
            class="flex justify-center q-pa-md"
          >
            <q-spinner size="2rem" color="grey-7" />
          </div>
          <q-item
            v-for="(option, i) in searchFilterOptions.engineType"
            :key="i + '_engineTypeSearchFilterOption'"
            :inset-level="0"
            v-ripple
            tag="label"
            dense
            :disable="!searchLoaded"
          >
            <q-item-section side>
              <q-checkbox
                dense
                v-model="searchFilterModels.engineType"
                :val="option.value"
                color="secondary"
                :disable="!searchLoaded"
                @click="filterSearchResults"
              />
            </q-item-section>
            <q-item-section>
              {{ option.label }}
            </q-item-section>
            <q-item-section side>
              <small v-if="searchLoaded">{{ option.count }}</small>
              <q-skeleton
                v-else
                type="text"
                class="text-subtitle1"
                animation="fade"
                style="width: 28px"
              />
            </q-item-section>
          </q-item>
        </q-expansion-item>
      </q-list>
    </div>
  </q-form>
</template>

<script>
import RangeWithInputs from "@/components/UI/RangeWithInputs.vue";
import ScoreBadge from "@/components/UI/ScoreBadge.vue";

export default {
  name: "FiltersSidebar",
  components: { ScoreBadge, RangeWithInputs },
  data() {
    return {
      tempPostalCode: null,
      location: {
        postalCodeInput: {
          error: null,
          disableSubmit: false,
          disabled: false
        }
      },
      searchMode: "classic"
    };
  },
  computed: {
    postalCode: {
      get() {
        return this.$store.state.search.location.postalCode;
      },
      set(value) {
        // Store the value temporarily for validation
        this.tempPostalCode = value;
      }
    },
    radius: {
      get() {
        return this.$store.state.search.location.radius;
      },
      set(value) {
        // Update store directly when radius changes
        this.$store.commit("search/SET_LOCATION", {
          postalCode: this.postalCode,
          city: this.$store.state.search.location.city,
          state: this.$store.state.search.location.state,
          radius: value
        });
        this.updateSearchLocation();
      }
    },
    searchFilterModels() {
      return this.$store.state.search.filter.models;
    },
    searchFilterOptions() {
      return this.$store.state.search.filter.options;
    },
    searchFilterModelDefaults() {
      return this.$store.state.search.filter.modelDefaults;
    },
    searchLoaded() {
      return this.$store.state.search.loaded;
    },
    featuresFilterOptionsLoading() {
      return this.$store.state.search.featuresFilterOptionsLoading;
    }
  },
  mounted() {},
  methods: {
    updateSearchLocation() {
      this.location.postalCodeInput.error = null;

      const postalCodeToValidate = this.tempPostalCode !== null ? this.tempPostalCode : this.postalCode;

      if (postalCodeToValidate.length < 5) {
        this.location.postalCodeInput.error = "Invalid zip code.";
        return;
      }

      this.$store
        .dispatch("geolocation/VALIDATE_POSTAL_CODE", postalCodeToValidate)
        .then(locationData => {
          this.location.postalCodeInput.error = null;

          this.$store.commit("search/SET_LOCATION", {
            postalCode: postalCodeToValidate,
            city: locationData["city"],
            state: locationData["state"],
            radius: this.radius
          });

          this.tempPostalCode = null;

          this.$store.commit("search/RESET_PAGINATION");
          // Apply filter.models to searchFilters
          this.$store.dispatch("search/APPLY_FILTER_MODELS");
          this.$store.commit("search/SET_LOADING");
        })
        .catch(error => {
          this.location.postalCodeInput.error = error;
        });
    },
    filterSearchResults() {
      this.$store
        .dispatch("search/CHECK_FILTER_MODELS_UPDATED")
        .then(updated => {
          if (updated) {
            this.$store.commit("search/RESET_PAGINATION");
            // Apply filter.models to searchFilters
            this.$store.dispatch("search/APPLY_FILTER_MODELS");
            this.$store.commit("search/SET_LOADING");
          }
        });
    },
    resetFilters() {
      this.$store.commit("search/RESET_PAGINATION");
      this.$store.commit("search/SET_SEARCH_FILTERS", []);
      this.$store.commit("search/SET_LOADING");
    }
  }
};
</script>
<style lang="scss" scoped>
.filters-sidebar {
  &::-webkit-scrollbar {
    display: none;
  }

  .filters-sidebar__card {
    background: color(background, light);
    position: relative;

    > .q-list > .q-expansion-item {
      border-bottom: 1px solid color(border);

      /*&:last-of-type {
        border-bottom: none;
      }*/
    }
  }
}
</style>
<style>
.q-expansion-item__content {
  padding: 4px 0 8px !important;
}
</style>
